import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import {
  CreateLimitationDefinitionBodySchema,
  type CreateLimitationDefinitionBody,
  type LimitationDefinition,
  type UpdateLimitationDefinitionBody,
} from "src/shared/api-types/limitations";

import Button from "src/components/Button";
import { captureHandledException } from "src/services/sentry";
import { getTranslatedApiError, toTranslationKey } from "src/utils/apiError";

import { getDefinitionDisplayName } from "../limitationDisplay";
import { ensureGeneratedCodes, getDefaultOptionForType, toFormValues } from "../limitationFormValues";
import type { LimitationFormValues } from "../types";

import LimitationValueEditor from "./LimitationValueEditor";

interface LimitationDefinitionDialogProps {
  open: boolean;
  definition?: LimitationDefinition | null;
  onClose: () => void;
  onSubmit: (body: CreateLimitationDefinitionBody | UpdateLimitationDefinitionBody) => Promise<void>;
}

const LimitationDefinitionDialog = ({ open, definition, onClose, onSubmit }: LimitationDefinitionDialogProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<LimitationFormValues>(toFormValues(definition ?? undefined));
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const structuralLocked = !!definition && definition.usageCount > 0;

  useEffect(() => {
    if (!open) return;
    const nextValues = toFormValues(definition ?? undefined);
    setValues(definition ? { ...nextValues, name: getDefinitionDisplayName(definition, t) } : nextValues);
    setSubmitError(null);
  }, [definition, open, t]);

  const structuralHelp = useMemo(() => {
    if (!structuralLocked) return null;
    return t("limitations.structural_fields_locked");
  }, [structuralLocked, t]);

  const handleFieldChange = <K extends keyof LimitationFormValues>(field: K, value: LimitationFormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (type: LimitationDefinition["type"]) => {
    setValues((prev) => ({
      ...prev,
      type,
      operator: type === "number" ? prev.operator || "<" : null,
      unit: type === "number" ? prev.unit : "",
      options: type === "boolean" ? prev.options : [getDefaultOptionForType(type)],
    }));
  };

  const buildBody = (): CreateLimitationDefinitionBody | UpdateLimitationDefinitionBody => {
    const generatedValues = ensureGeneratedCodes(values);
    const isNumber = generatedValues.type === "number";
    return {
      code: generatedValues.code,
      name: generatedValues.name,
      type: generatedValues.type,
      operator: isNumber ? generatedValues.operator || "<" : null,
      unit: isNumber ? generatedValues.unit || null : null,
      sortOrder: generatedValues.sortOrder,
      options: generatedValues.options.map((option) => ({
        ...(option.id && { id: option.id }),
        value: option.value,
        sortOrder: option.sortOrder,
        ...(option.archived !== undefined && { archived: option.archived }),
      })),
    };
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSubmitError(null);
    try {
      const body = buildBody();
      const validation = CreateLimitationDefinitionBodySchema.safeParse(body);
      if (!validation.success) {
        setSubmitError(t(toTranslationKey(validation.error.issues[0]?.message ?? "limitations.errors.save_failed")));
        return;
      }
      await onSubmit(body);
      onClose();
    } catch (err) {
      captureHandledException(err, {
        feature: "limitations",
        action: definition ? "updateDefinition" : "createDefinition",
        limitationId: definition?.id,
      });
      setSubmitError(await getTranslatedApiError(err, t, "limitations.errors.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{definition ? t("limitations.edit_title") : t("limitations.add_title")}</DialogTitle>
      <DialogContent>
        {structuralHelp && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {structuralHelp}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
          <TextField
            size="small"
            label={t("limitations.name")}
            value={values.name}
            onChange={(event) => handleFieldChange("name", event.target.value)}
            autoComplete="off"
          />
          <TextField
            select
            size="small"
            label={t("limitations.type")}
            value={values.type}
            onChange={(event) => handleTypeChange(event.target.value as LimitationDefinition["type"])}
            disabled={structuralLocked}
          >
            <MenuItem value="number">{t("limitations.types.number")}</MenuItem>
            <MenuItem value="list">{t("limitations.types.list")}</MenuItem>
            <MenuItem value="boolean">{t("limitations.types.boolean")}</MenuItem>
          </TextField>
          {values.type === "number" && (
            <TextField
              select
              size="small"
              label={t("limitations.operator")}
              value={values.operator === ">" ? ">" : "<"}
              onChange={(event) =>
                handleFieldChange("operator", event.target.value as LimitationDefinition["operator"])
              }
              disabled={structuralLocked}
            >
              <MenuItem value="<">{t("limitations.operators.less_than")}</MenuItem>
              <MenuItem value=">">{t("limitations.operators.greater_than")}</MenuItem>
            </TextField>
          )}
          {values.type === "number" && (
            <TextField
              size="small"
              label={t("limitations.unit")}
              value={values.unit}
              onChange={(event) => handleFieldChange("unit", event.target.value)}
              autoComplete="off"
            />
          )}
        </Box>
        {values.type === "boolean" ? (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info">{t("limitations.boolean_values_hint")}</Alert>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <LimitationValueEditor
              type={values.type}
              options={values.options}
              structuralLocked={structuralLocked}
              onChange={(options) => handleFieldChange("options", options)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={saving || !values.name}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LimitationDefinitionDialog;
